// components/HamburgerMenu.js
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./HamburgerMenu.css";

const HamburgerMenu = ({ onLogout, onProfileClick, onAboutClick, onMapClick, onCommunityClick }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const menuRef = useRef(null);
    const toggleRef = useRef(null);

    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

    const currentLang = i18n.language?.substring(0, 2) || 'fr';

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    // Ferme le menu si on change de route
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Fermer le menu quand on clique en dehors
    useEffect(() => {
        const handlePointerDown = (event) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, []);

    const safeCall = (fn) => {
        if (typeof fn === "function") fn();
    };

    const computeMenuPos = () => {
        const btn = toggleRef.current;
        if (!btn) return;

        const r = btn.getBoundingClientRect();
        const gap = 6;

        // On place le menu sous le bouton.
        // "left" est en fait le bord droit du bouton, car en CSS on fera translateX(-100%)
        const top = r.bottom + gap;
        const left = r.right;

        setMenuPos({ top, left });
    };

    // À l'ouverture: calcule la position + recalcule si resize/scroll (parents inclus)
    useLayoutEffect(() => {
        if (!open) return;

        computeMenuPos();

        const onRelayout = () => computeMenuPos();
        window.addEventListener("resize", onRelayout);
        window.addEventListener("scroll", onRelayout, true);

        return () => {
            window.removeEventListener("resize", onRelayout);
            window.removeEventListener("scroll", onRelayout, true);
        };
    }, [open]);

    const handleToggle = (e) => {
        e.stopPropagation();
        setOpen((v) => !v);
    };

    return (
        <div className="profile-menu" ref={menuRef}>
            <button
                ref={toggleRef}
                className="menu-toggle"
                onClick={handleToggle}
                aria-label="Menu"
                type="button"
            >
                {open ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <line x1="3" y1="12" x2="21" y2="12"></line>
                        <line x1="3" y1="6" x2="21" y2="6"></line>
                        <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                )}
            </button>

            <div
                className={`menu-dropdown ${open ? "open" : ""}`}
                style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
            >
                <button
                    onClick={() => {
                        setOpen(false);
                        safeCall(onMapClick);
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.home')}
                </button>

                <button
                    onClick={() => {
                        setOpen(false);
                        navigate("/dives");
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.dives')}
                </button>

                <button
                    onClick={() => {
                        setOpen(false);
                        safeCall(onProfileClick);
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.profile')}
                </button>

                <button
                    onClick={() => {
                        setOpen(false);
                        safeCall(onAboutClick);
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.about')}
                </button>

                <button
                    onClick={() => {
                        setOpen(false);
                        safeCall(onCommunityClick);
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.community')}
                </button>

                <button
                    onClick={() => {
                        setOpen(false);
                        safeCall(onLogout);
                    }}
                    type="button"
                >
                    {t('hamburgerMenu.logout')}
                </button>

                {/* Sélecteur de langue */}
                <div className="language-selector">
                    <button
                        type="button"
                        className={currentLang === 'fr' ? 'active' : ''}
                        onClick={() => changeLanguage('fr')}
                    >
                        FR
                    </button>
                    <button
                        type="button"
                        className={currentLang === 'de' ? 'active' : ''}
                        onClick={() => changeLanguage('de')}
                    >
                        DE
                    </button>
                    <button
                        type="button"
                        className={currentLang === 'it' ? 'active' : ''}
                        onClick={() => changeLanguage('it')}
                    >
                        IT
                    </button>
                    <button
                        type="button"
                        className={currentLang === 'en' ? 'active' : ''}
                        onClick={() => changeLanguage('en')}
                    >
                        EN
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HamburgerMenu;
